import { Component } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, of } from 'rxjs';

import { UserService } from 'src/app/services/user.service';
import { MemberService } from 'src/app/services/member.service';
import { ExportService } from 'src/app/services/export.service';
import { AlertService } from 'src/app/services/alert.service';
import { FacilityV2Service } from 'src/app/services/facilityV2.service';
import { ConfigService } from 'src/app/services/config.service';
import { ExportStatementsComponent, ExportStatementsResult } from 'src/app/components/popups/export-statements/export-statements.component';
import { ExportCheckinsComponent, ExportCheckinsResult } from 'src/app/components/popups/export-checkins/export-checkins.component';
import { InfoDialogComponent } from 'src/app/components/popups/info-dialog/info-dialog.component';

@Component({
  selector: 'df-account-reports',
  templateUrl: './account-reports.component.html',
  styleUrls: ['./account-reports.component.scss']
})
export class AccountReportsComponent {
  isExporting = false;

  constructor(
    private modalService: NgbModal,
    private userService: UserService,
    private memberService: MemberService,
    private exportService: ExportService,
    private alertService: AlertService,
    private facilityV2Service: FacilityV2Service,
    private configService: ConfigService
  ) {}

  openExportStatements(): void {
    const user = this.userService.getUser();
    const memberName = user ? `${user.FirstName} ${user.LastName}` : 'Member';

    const modalRef = this.modalService.open(ExportStatementsComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'md'
    });

    modalRef.componentInstance.memberName = memberName;

    modalRef.closed.subscribe(async (result: ExportStatementsResult) => {
      if (!result) return;
      await this.exportStatements(result);
    });
  }

  openExportCheckins(): void {
    const user = this.userService.getUser();
    const memberName = user ? `${user.FirstName} ${user.LastName}` : 'Member';

    const modalRef = this.modalService.open(ExportCheckinsComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'md'
    });

    modalRef.componentInstance.memberName = memberName;

    modalRef.closed.subscribe(async (result: ExportCheckinsResult) => {
      if (!result) return;
      await this.exportCheckins(result);
    });
  }

  private async exportStatements(result: ExportStatementsResult): Promise<void> {
    if (this.isExporting) return;

    const member = this.userService.getMember();
    if (!member?.Id) {
      this.alertService.error('Unable to export: member not found.');
      return;
    }

    try {
      this.isExporting = true;

      const startDate = result.startDate || undefined;
      const endDate = result.endDate || undefined;

      const transactions = await firstValueFrom(
        this.memberService.getPaymentTransactionsByDates(member.Id, startDate, endDate)
      );

      const memberAccount = await firstValueFrom(
        this.userService.fetchMemberAccount(false) || of(null)
      );

      const memberName = memberAccount?.Person?.FullName || memberNameFromUser(this.userService.getUser());

      if (result.format === 'csv') {
        this.exportService.generateCSV(transactions, memberName);
      } else {
        const facilityName = this.configService.getConfig()?.name || 'Drop Fitness';
        const location = await this.facilityV2Service.getLocationById(member.LocationId);

        this.exportService.generatePDF({
          transactions,
          memberAccount,
          facilityName,
          location
        });
      }

      this.alertService.success('Export completed successfully.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      this.alertService.error('Export failed. Please try again.');
    } finally {
      this.isExporting = false;
    }
  }

  private async exportCheckins(result: ExportCheckinsResult): Promise<void> {
    if (this.isExporting) return;

    const member = this.userService.getMember();
    if (!member?.Id) {
      this.alertService.error('Unable to export: member not found.');
      return;
    }

    try {
      this.isExporting = true;

      const checkinDates = await firstValueFrom(
        this.memberService.getAccessCheckinsByMemberAndDates(member.Id, result.startDate, result.endDate)
      );

      if (!checkinDates || checkinDates.length === 0) {
        const infoRef = this.modalService.open(InfoDialogComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false,
          size: 'sm'
        });
        infoRef.componentInstance.title = 'Export check-ins';
        infoRef.componentInstance.message = 'No check-ins in that period';
        return;
      }

      const memberAccount = await firstValueFrom(
        this.userService.fetchMemberAccount(false) || of(null)
      );

      const facilityName = this.configService.getConfig()?.name || 'Drop Fitness';
      const location = await this.facilityV2Service.getLocationById(member.LocationId);

      if (result.format === 'csv') {
        this.exportService.generateCheckinsCSV(checkinDates, memberAccount);
      } else {
        this.exportService.generateCheckinsPDF({
          facilityName,
          location,
          memberAccount,
          checkinDates
        });
      }

      this.alertService.success('Export completed successfully.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      this.alertService.error('Export failed. Please try again.');
    } finally {
      this.isExporting = false;
    }
  }
}

function memberNameFromUser(user: any): string {
  return user ? `${user.FirstName} ${user.LastName}` : 'member';
}
